import { authFetch, getAuthHeaders } from '../lib/authFetch';

const getBaseUrl = (url: string) => url.startsWith('http') ? url : `https://${url}`;

const API_URL = import.meta.env.VITE_API_BASE_URL
    ? `${getBaseUrl(import.meta.env.VITE_API_BASE_URL)}/api/trades`
    : 'https://stingray-app-2pifp.ondigitalocean.app/api/trades';

export const openTrade = async (tradeData: { asset: string, type: string, side: string, amount: number, price?: number, entryPrice?: number, leverage?: number, orderType?: string, limitPrice?: number, stopLoss?: number, takeProfit?: number }): Promise<any> => {
    const response = await authFetch(`${API_URL}/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(tradeData),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to open trade');
    }
    return response.json();
};

// Admin: open a trade on behalf of a specific user
export const adminOpenTrade = async (tradeData: {
    userId: string;
    asset: string;
    type: string;
    side: string;
    amount: number;
    price?: number;
    entryPrice?: number;
    leverage?: number;
}): Promise<any> => {
    const adminUrl = import.meta.env.VITE_API_BASE_URL
        ? `${getBaseUrl(import.meta.env.VITE_API_BASE_URL)}/api/admin/trades`
        : 'https://stingray-app-2pifp.ondigitalocean.app/api/admin/trades';
    const response = await authFetch(`${adminUrl}/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(tradeData),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to open trade for user');
    }
    return response.json();
};

export const getMyOpenTrades = async (status?: string): Promise<any[]> => {
    const url = status ? `${API_URL}/?status=${encodeURIComponent(status)}` : `${API_URL}/`;
    const response = await authFetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to fetch open trades');
    }
    return response.json();
};

export const closeTrade = async (tradeId: string): Promise<any> => {
    const response = await authFetch(`${API_URL}/${tradeId}/close`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to close trade');
    }
    return response.json();
};

// Update stop loss / take profit on an existing open position — pass null to clear a value
export const setTradeTPSL = async (tradeId: string, updates: { stopLoss?: number | null; takeProfit?: number | null }): Promise<any> => {
    const response = await authFetch(`${API_URL}/${tradeId}/tpsl`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to update stop loss / take profit');
    }
    return response.json();
};

// Get market prices — via backend (cached, multi-source fallback)
export const getMarketPrices = async (symbols?: string[]): Promise<Record<string, number>> => {
    try {
        const query = symbols ? `?symbols=${symbols.join(',')}` : '';
        const response = await fetch(`${API_URL}/prices${query}`);
        if (response.ok) {
            const data = await response.json();
            const hasRealPrices = data && Object.values(data).some((v) => (v as number) > 0);
            if (hasRealPrices) return data;
        }
    } catch { /* ignore */ }

    return {};
};

// Get detailed market data (price, 24h change, volume, market cap)
export const getDetailedMarketData = async (symbols?: string[]): Promise<Record<string, any>> => {
    const query = symbols ? `?symbols=${symbols.join(',')}` : '';
    const response = await fetch(`${API_URL}/market-data${query}`);
    if (!response.ok) {
        throw new Error('Failed to fetch market data');
    }
    return response.json();
};

// Get closed/historical trades (closed = manually closed, liquidated = force-closed by system)
export const getMyClosedTrades = async (): Promise<any[]> => {
    const response = await authFetch(`${API_URL}/?status=closed,liquidated`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to fetch closed trades');
    }
    return response.json();
};
